import {
  Box,
  Container,
  Flex,
  Link,
  Stack,
  Text,
  VStack,
  Icon,
  Divider,
  SimpleGrid,
  chakra,
  Tooltip,
} from '@chakra-ui/react';
import { 
  IoLogoFacebook,
  IoLogoInstagram,
  IoLogoLinkedin,
  IoMail,
  IoLocationSharp,
} from "react-icons/io5";

// 1. Data Configuration (Fácil de mantener o mover a un archivo .json)
const SOCIAL_LINKS = [
  { label: 'Facebook', href: 'https://www.facebook.com/people/Secretaria-De-Educacion/61552160237769', icon: IoLogoFacebook },
  { label: 'Instagram', href: 'https://www.instagram.com/secretariadeeducacion_goya/', icon: IoLogoInstagram },
  { label: 'Email', href: 'mailto:secretariadeeducaciongoya@gmail.com', icon: IoMail },
];

const QUICK_LINKS = [
  { label: 'Inicio', href: '/' },
  { label: 'Carreras', href: '/carreras' },
  { label: 'Instituciones', href: '/instituciones' },
  { label: 'Calendario', href: '/calendario' },
];

export default function FooterComponent() {
  const currentYear = new Date().getFullYear();

  return (
    <chakra.footer
      width="full"
      bg="rgba(10, 25, 47, 0.4)" // Un tono más profundo para el glassmorphism
      backdropFilter="blur(15px)"
      borderTop="1px solid"
      borderColor="whiteAlpha.100"
      py={{ base: 12, md: 16 }}
      mt="auto"
      color="whiteAlpha.900"
    >
      <Container maxW="container.xl">
        <SimpleGrid 
          columns={{ base: 1, md: 3 }} 
          spacing={12} 
          mb={10}
        >
          {/* Columna 1: Branding y Ubicación */}
          <VStack align={{ base: 'center', md: 'flex-start' }} spacing={5}>
            <Box>
              <Text fontSize="3xl" fontWeight="900" lineHeight="1" letterSpacing="tight">
                Goya<Text as="span" color="yellow.400">Educa</Text>
              </Text>
              <Text mt={2} fontSize="sm" color="whiteAlpha.600" fontWeight="medium">
                Secretaría de Educación <br /> Municipalidad de Goya
              </Text>
            </Box>
            
            <Flex align="center" fontSize="xs" color="whiteAlpha.500">
              <Icon as={IoLocationSharp} mr={2} color="yellow.500" />
              <Text>Goya, Corrientes, Argentina</Text>
            </Flex>
          </VStack>

          {/* Columna 2: Navegación Rápida */}
          <VStack spacing={4} align={{ base: 'center', md: 'flex-start' }}>
            <Text fontWeight="bold" fontSize="md" color="white" textTransform="uppercase" letterSpacing="wider">
              Mapa del Sitio
            </Text>
            <Stack direction={{ base: 'row', md: 'column' }} spacing={3} align={{ base: 'center', md: 'flex-start' }}>
              {QUICK_LINKS.map((link) => (
                <Link 
                  key={link.label}
                  href={link.href}
                  fontSize="sm"
                  _hover={{ color: 'yellow.400', textDecoration: 'none' }}
                  transition="color 0.2s"
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </VStack>

          {/* Columna 3: Redes y Contacto */}
          <VStack spacing={4} align={{ base: 'center', md: 'flex-end' }}>
            <Text fontWeight="bold" fontSize="md" color="white" textTransform="uppercase" letterSpacing="wider">
              Conectemos
            </Text>
            <Stack direction="row" spacing={4}>
              {SOCIAL_LINKS.map((social) => (
                <SocialIcon key={social.label} {...social} />
              ))}
            </Stack>
          </VStack>
        </SimpleGrid>

        <Divider borderColor="whiteAlpha.100" />

        {/* Bottom Bar */}
        <Flex 
          direction={{ base: 'column', md: 'row' }} 
          justify="space-between" 
          align="center" 
          pt={8}
          fontSize="xs"
          color="whiteAlpha.400"
          gap={4}
        >
          <Text textAlign={{ base: 'center', md: 'left' }}>
            © {currentYear} Municipalidad de Goya. Todos los derechos reservados.
          </Text>
          <Text fontWeight="bold" letterSpacing="widest" color="whiteAlpha.600">
            DESARROLLO TECNOLÓGICO
          </Text>
        </Flex>
      </Container>
    </chakra.footer>
  );
}

// 2. Componente de Redes con Tooltip y Animación Senior
const SocialIcon = ({ href, icon, label }: { href: string; icon: any; label: string }) => (
  <Tooltip label={label} bg="gray.700" color="white" fontSize="xs" hasArrow>
    <Link 
      href={href} 
      isExternal 
      p={3}
      bg="whiteAlpha.50"
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      transition="all 0.3s cubic-bezier(.64,.09,.08,1)"
      _hover={{ 
        bg: "yellow.400", 
        color: "blue.900", // Contraste invertido al hover
        transform: "scale(1.1) rotate(5deg)",
        boxShadow: "0 0 15px rgba(236, 201, 75, 0.4)" 
      }}
    >
      <Icon as={icon} boxSize={5} />
    </Link>
  </Tooltip>
);